class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', this.onSubmit.bind(this));

    this.heroOfferCheck = this.querySelector('[data-hero-addon-check]');
    this.toteOfferCheck = this.querySelector('[data-tote-addon-check]');
    this.kitCheckboxes = this.querySelectorAll('[data-kit-addon]');
    this.personalisationInput = this.querySelector('#PersonalisationText');
    this.personalisationToggle = this.querySelector('#PersonalisationToggle');
    this.dynamicCheckout = this.querySelector('[data-dynamic-checkout]');
    this.dynamicNote = this.querySelector('[data-dynamic-note]');

    // Colour / variant selection: update image, label, price and button state
    this.querySelectorAll('input[name="id"][type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => this.onVariantChange(radio));
    });

    // Quantity stepper
    this.qtyInput = this.querySelector('input[name="quantity"]');
    this.querySelectorAll('[data-qty-step]').forEach((b) => b.addEventListener('click', () => {
      if (!this.qtyInput) return;
      const next = Math.min(99, Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + parseInt(b.dataset.qtyStep, 10)));
      this.qtyInput.value = next;
    }));

    // Express checkout only buys the main variant, so hide it while extras are ticked
    this.form.addEventListener('change', () => this.syncExpressCheckout());

    // Second-tote swatch cards: tick the offer, show the choice and swap the card photo
    this.toteRadios = this.querySelectorAll('input[name="tote_second_variant"]');
    this.toteChosen = this.querySelector('[data-tote-chosen]');
    const syncTote = (radio, fromUser) => {
      if (!radio) return;
      if (this.toteChosen) this.toteChosen.textContent = radio.dataset.title || '';
      const card = radio.closest('[data-tote-card]');
      const img = card && card.querySelector('[data-tote-img]');
      if (img && radio.dataset.image) img.src = radio.dataset.image;
      if (fromUser && this.toteOfferCheck && !this.toteOfferCheck.checked) {
        this.toteOfferCheck.checked = true;
        this.form.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    this.toteRadios.forEach((radio) => radio.addEventListener('change', () => syncTote(radio, true)));
    syncTote(this.querySelector('input[name="tote_second_variant"]:checked'), false);

    // Two-choice bundles: the option rows pick the matching variant
    const multi = this.querySelector('[data-multi-option]');
    if (multi) {
      multi.addEventListener('change', () => {
        const sets = [...multi.querySelectorAll('fieldset')];
        const picked = sets.map((fs) => (fs.querySelector('input:checked') || {}).value || '');
        sets.forEach((fs, i) => { const l = fs.querySelector('[data-opt-label]'); if (l) l.textContent = picked[i]; });
        const radio = [...this.querySelectorAll('input[name="id"][data-options]')].find((r) => r.dataset.options === picked.join('|'));
        const btn = this.querySelector('.main-add-to-bag');
        if (radio) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
        else if (btn) { btn.disabled = true; btn.textContent = 'Not available in this combination'; }
      });
    }

    // Second Hero: tick the first choice and show its name
    this.pairChosen = this.querySelector('[data-pair-chosen]');
    const pairRadios = [...this.querySelectorAll('input[name="hero_second_variant"]')];
    if (pairRadios.length && !pairRadios.some((r) => r.checked)) pairRadios[0].checked = true;
    const syncPair = () => {
      const r = this.querySelector('input[name="hero_second_variant"]:checked');
      if (this.pairChosen && r) this.pairChosen.textContent = r.dataset.name || '';
    };
    pairRadios.forEach((r) => r.addEventListener('change', syncPair));
    syncPair();

    // Complete your kit colour circles: the row's checkbox carries the chosen colour
    this.querySelectorAll('[data-kit-variant]').forEach((radio) => radio.addEventListener('change', () => {
      const row = radio.closest('[data-kit-row]');
      if (!row) return;
      const cb = row.querySelector('[data-kit-addon]');
      const img = row.querySelector('[data-kit-img]');
      const label = row.querySelector('[data-kit-colour]');
      if (cb) { cb.value = radio.value; cb.checked = true; }
      if (img && radio.dataset.image) img.src = radio.dataset.image;
      if (label) label.textContent = radio.dataset.title || '';
      this.form.dispatchEvent(new Event('change', { bubbles: true }));
    }));

    // The buy box reads as a sequence (colour, second bag, kit) without step numbers. Using a later step makes it
    // the current one; picking a colour, or the second bag's colour, hands over to the next. Add to bag never waits on it
    this.steps = [...this.querySelectorAll('[data-step]')];
    if (this.steps.length > 1) {
      this.form.classList.add('has-steps');
      let at = 0;
      const mark = () => this.steps.forEach((s, i) => {
        s.classList.toggle('is-current', i === at);
        s.classList.toggle('is-done', i < at);
      });
      const reach = (i) => { const next = Math.min(i, this.steps.length - 1); if (next > at) { at = next; mark(); } };
      const finishes = 'input[name="id"], [name^="opt-"], input[name="hero_second_variant"], input[name="tote_second_variant"]';
      this.steps.forEach((s, i) => {
        s.addEventListener('pointerdown', () => reach(i));
        s.addEventListener('focusin', () => reach(i));
        s.addEventListener('change', (e) => reach(e.target.matches(finishes) ? i + 1 : i));
      });
      mark();
    }

    // The back-in-stock box (Swym app) arrives late at the foot of the column; move it under the buy buttons
    this.bisAnchor = this.querySelector('[data-bis-anchor]');
    if (this.bisAnchor) {
      const moveBis = () => {
        const box = [...document.querySelectorAll('[class*="swym"], [id*="swym"]')]
          .find((el) => el.tagName !== 'SCRIPT' && el.tagName !== 'STYLE' && el.querySelector('input[type="email"]') && !this.bisAnchor.contains(el));
        if (!box) return false;
        let top = box;
        while (top.parentElement && /swym/i.test(top.parentElement.className + top.parentElement.id) && !top.parentElement.contains(this.form)) top = top.parentElement;
        // a wrapper that holds the anchor itself can't be moved into it (appendChild throws, and the watcher kept retrying)
        if (top.contains(this.bisAnchor)) top = box;
        if (box.contains(this.bisAnchor)) return true;
        this.bisAnchor.appendChild(top);
        return true;
      };
      if (!moveBis()) {
        const mo = new MutationObserver(() => { if (moveBis()) mo.disconnect(); });
        mo.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => mo.disconnect(), 30000);
      }
    }

    const preview = () => document.querySelector('[data-personalisation-preview]');
    if (this.personalisationToggle && this.personalisationInput) {
      this.personalisationToggle.addEventListener('change', (e) => {
        this.personalisationInput.disabled = !e.target.checked;
        if (e.target.checked) this.personalisationInput.focus();
        if (preview()) preview().textContent = e.target.checked ? this.personalisationInput.value.toUpperCase() : '';
      });
      this.personalisationInput.addEventListener('input', (e) => {
        if (preview()) preview().textContent = e.target.value.toUpperCase();
      });
    }
  }

  hasExtras() {
    return Boolean(
      this.heroOfferCheck?.checked ||
      this.toteOfferCheck?.checked ||
      this.personalisationToggle?.checked ||
      [...this.kitCheckboxes].some((cb) => cb.checked)
    );
  }

  syncButtonLabel() {
    const btn = this.querySelector('.main-add-to-bag');
    if (!btn || btn.disabled) return;
    const checked = this.querySelector('input[name="id"]:checked');
    const price = checked ? checked.dataset.price : null;
    if (!this.baseLabel || price) this.baseLabel = price ? `Add to bag · ${price}` : (this.baseLabel || btn.textContent.trim());
    const offer = [this.heroOfferCheck, this.toteOfferCheck].find((c) => c && c.checked);
    const extras = this.personalisationToggle?.checked || [...this.kitCheckboxes].some((cb) => cb.checked);
    let label = offer ? offer.dataset.pairLabel : this.baseLabel;
    if (extras) label += ' + extras';
    btn.textContent = label;
  }

  syncExpressCheckout() {
    this.syncButtonLabel();
    if (!this.dynamicCheckout) return;
    const extras = this.hasExtras();
    const checked = this.querySelector('input[name="id"]:checked');
    const soldOut = checked && checked.dataset.available === 'false';
    this.dynamicCheckout.classList.toggle('is-hidden', extras || soldOut);
    if (this.dynamicNote) this.dynamicNote.hidden = !extras || soldOut;
  }

  onVariantChange(radio) {
    const label = document.getElementById('SelectedVariantLabel');
    if (label) label.textContent = radio.dataset.title;
    document.querySelectorAll('[data-sticky-variant]').forEach((el) => { el.textContent = radio.dataset.title; });
    const img = document.getElementById('ProductMainImage');
    if (img && radio.dataset.image) img.src = radio.dataset.image;
    const stickyImg = document.querySelector('[data-sticky-img]');
    if (stickyImg && radio.dataset.image) stickyImg.src = radio.dataset.image;
    document.querySelectorAll('[data-current-price]').forEach((el) => { if (radio.dataset.price) el.textContent = radio.dataset.price; });
    const available = radio.dataset.available === 'true';
    const btn = this.querySelector('.main-add-to-bag');
    if (btn) {
      btn.disabled = !available;
      btn.textContent = available ? `Add to bag · ${radio.dataset.price}` : 'Sold out';
      this.baseLabel = available ? `Add to bag · ${radio.dataset.price}` : null;
      if (available) this.syncButtonLabel();
    }
    const stickyBtn = document.querySelector('.sticky-atc__btn');
    if (stickyBtn) { stickyBtn.disabled = !available; stickyBtn.textContent = available ? 'Add to bag' : 'Sold out'; }
    this.syncExpressCheckout();
    const url = new URL(window.location.href);
    url.searchParams.set('variant', radio.value);
    window.history.replaceState({}, '', url.toString());
  }

  async onSubmit(e) {
    e.preventDefault();
    const submitBtn = this.querySelector('[type="submit"]');
    const originalText = submitBtn.textContent;
    submitBtn.disabled = true;
    submitBtn.classList.add('is-adding');
    submitBtn.setAttribute('aria-busy', 'true');
    submitBtn.textContent = 'Adding...';
    let added = false;

    const formData = new FormData(this.form);
    const mainVariantId = formData.get('id');
    const items = [];

    const properties = {};
    const wantsName = this.personalisationToggle?.checked && this.personalisationInput?.value.trim() !== '';
    if (wantsName) properties['Personalisation Text'] = this.personalisationInput.value.trim().toUpperCase();

    const qty = Math.min(99, Math.max(1, parseInt(formData.get('quantity'), 10) || 1));
    items.push({ id: parseInt(mainVariantId, 10), quantity: qty, properties });

    if (wantsName && this.personalisationToggle.dataset.productId) {
      items.push({
        id: parseInt(this.personalisationToggle.dataset.productId, 10),
        quantity: 1,
        properties: { 'For item': formData.get('product-title') || '' }
      });
    }

    const secondHero = this.querySelector('input[name="hero_second_variant"]:checked');
    if (this.heroOfferCheck?.checked && secondHero) {
      items.push({ id: parseInt(secondHero.value, 10), quantity: 1 });
    }

    const secondTote = this.querySelector('input[name="tote_second_variant"]:checked');
    if (this.toteOfferCheck?.checked && secondTote) {
      items.push({ id: parseInt(secondTote.value, 10), quantity: 1 });
    }

    this.kitCheckboxes.forEach((cb) => {
      if (cb.checked) items.push({ id: parseInt(cb.value, 10), quantity: 1 });
    });

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      if (res.ok) {
        added = true;
        submitBtn.textContent = 'Added ✓';
        const drawer = document.querySelector('cart-drawer');
        if (drawer) { await drawer.refresh(); drawer.open(); }
      } else {
        const err = await res.json();
        alert(err.description || 'Sorry, that could not be added to your bag.');
      }
    } catch (err) {
      console.error('Add to bag error:', err);
    } finally {
      // the button stays inverted (white, lime border) while adding and for a moment after, so the action is obvious
      const restore = () => {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-adding');
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = originalText;
      };
      if (added) setTimeout(restore, 1200); else restore();
    }
  }
}
customElements.define('product-form', ProductForm);

// Product gallery (moved here from main-product.liquid, 1 Nov): colour-grouped photo sets, arrows, swipe, arrow keys and swatch hover preview
function initProductGallery() {
  if (!document.querySelector('.product-gallery')) return;
  const main = document.getElementById('ProductMainImage');
  const allThumbs = [...document.querySelectorAll('.product-gallery__thumbs .thumb-btn')];
  const tiles = [...document.querySelectorAll('.product-gallery__tile')];
  const count = document.querySelector('[data-gallery-count]');
  const arrows = [...document.querySelectorAll('[data-gallery-step]')];
  let thumbs = allThumbs;
  let cur = 0;
  let chosen = main ? main.src : '';
  let groupKey = null;

  // The set on show: the chosen colour's photos, then the general ones. Products without colour groups keep every photo
  const setGroup = (g) => {
    const own = allThumbs.filter((t) => t.dataset.group === g);
    const key = g && g !== 'all' && own.length ? g : 'all';
    if (key === groupKey) return false;
    groupKey = key;
    thumbs = key === 'all' ? allThumbs : own.concat(allThumbs.filter((t) => t.dataset.group === 'all'));
    allThumbs.forEach((t) => { t.hidden = true; t.style.order = ''; });
    thumbs.forEach((t, i) => { t.hidden = false; t.style.order = i; t.setAttribute('aria-label', `Show image ${i + 1} of ${thumbs.length}`); });
    arrows.forEach((a) => { a.hidden = thumbs.length < 2; });
    if (count) count.hidden = thumbs.length < 2;
    return true;
  };
  // The 2×2 tiles show the next four photos of the set
  const syncTiles = () => {
    const next = thumbs.slice(1, 1 + tiles.length);
    tiles.forEach((tile, k) => {
      const t = next[k];
      tile.hidden = !t;
      if (!t) return;
      tile.dataset.galleryId = t.dataset.id;
      const img = tile.querySelector('img');
      if (img && img.getAttribute('src') !== t.dataset.tile) { img.src = t.dataset.tile; img.alt = t.dataset.alt; }
    });
  };
  const show = (i) => {
    if (!thumbs.length || !main) return;
    cur = (i + thumbs.length) % thumbs.length;
    const t = thumbs[cur];
    main.src = t.dataset.full; main.alt = t.dataset.alt;
    chosen = main.src;
    allThumbs.forEach((b) => b.classList.toggle('is-active', b === t));
    const rail = t.parentElement;
    if (rail && !t.hidden) {
      if (rail.scrollHeight > rail.clientHeight) rail.scrollTop = t.offsetTop - rail.offsetTop - rail.clientHeight / 2 + t.offsetHeight / 2;
      if (rail.scrollWidth > rail.clientWidth) rail.scrollLeft = t.offsetLeft - rail.offsetLeft - rail.clientWidth / 2 + t.offsetWidth / 2;
    }
    if (count) count.textContent = `${cur + 1} / ${thumbs.length}`;
  };
  const step = (d) => { if (thumbs.length > 1) show(cur + d); };

  const startRadio = document.querySelector('#MainProductForm input[name="id"][type="radio"]:checked');
  setGroup(startRadio ? startRadio.dataset.group : 'all');
  const startAt = thumbs.findIndex((t) => t.classList.contains('is-active'));
  if (startAt >= 0) show(startAt); else if (thumbs.length) cur = 0;

  const mainBox = document.querySelector('.product-gallery__main');
  if (mainBox && allThumbs.length > 1) {
    let x0 = null, y0 = null;
    mainBox.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    mainBox.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) step(dx < 0 ? 1 : -1);
      x0 = null;
    }, { passive: true });
  }
  allThumbs.forEach((t) => t.addEventListener('click', () => show(thumbs.indexOf(t))));
  tiles.forEach((b) => b.addEventListener('click', () => {
    const i = thumbs.findIndex((t) => t.dataset.id === b.dataset.galleryId);
    if (i >= 0) show(i);
    if (mainBox) window.scrollTo({ top: mainBox.getBoundingClientRect().top + window.scrollY - 140, behavior: 'smooth' });
  }));
  arrows.forEach((b) => b.addEventListener('click', () => step(parseInt(b.dataset.galleryStep, 10))));

  // Left and right arrow keys move the gallery, unless someone is typing or choosing in a field
  document.addEventListener('keydown', (e) => {
    if ((e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const el = document.activeElement;
    if (el && (el.isContentEditable || /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName) || el.closest('[role="dialog"], dialog, [role="tablist"], [role="slider"]'))) return;
    if (document.querySelector('cart-drawer.is-open') || thumbs.length < 2) return;
    e.preventDefault();
    step(e.key === 'ArrowRight' ? 1 : -1);
  });

  // Hovering (or keyboard focus on) a colour previews its photo; leaving puts the chosen photo back. Mouse devices only
  const canHover = window.matchMedia('(hover: hover)');
  const variantRadios = [...document.querySelectorAll('#MainProductForm input[name="id"][type="radio"]')];
  const imageFor = (input) => {
    if (input.name === 'id') return input.dataset.image;
    const sets = [...document.querySelectorAll('[data-multi-option] fieldset')];
    const at = sets.findIndex((fs) => fs.contains(input));
    if (at < 0) return null;
    const picked = sets.map((fs, i) => (i === at ? input.value : (fs.querySelector('input:checked') || {}).value || ''));
    const match = variantRadios.find((r) => r.dataset.options === picked.join('|'))
      || variantRadios.find((r) => (r.dataset.options || '').split('|')[at] === input.value);
    return match ? match.dataset.image : null;
  };
  if (main) {
    document.querySelectorAll('#MainProductForm .module-variant-picker .swatch-label').forEach((label) => {
      const input = label.querySelector('input');
      if (!input) return;
      const preview = () => { const src = canHover.matches && imageFor(input); if (src) main.src = src; };
      const restore = () => { if (canHover.matches && chosen) main.src = chosen; };
      label.addEventListener('mouseenter', preview);
      label.addEventListener('mouseleave', restore);
      input.addEventListener('focus', preview);
      input.addEventListener('blur', restore);
    });
  }
  // A new colour resets the gallery to that colour's set, starting on its photo (runs after ProductForm has swapped the main photo)
  variantRadios.forEach((r) => r.addEventListener('change', () => {
    const changed = setGroup(r.dataset.group || 'all');
    if (changed) syncTiles();
    const i = thumbs.findIndex((t) => t.dataset.full === r.dataset.image);
    if (i >= 0) show(i);
    else if (changed) show(0);
    else if (main) chosen = main.src;
  }));
}
document.addEventListener('DOMContentLoaded', initProductGallery);
